import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ApiService } from '../services/api.service';

type Band = { bandId: string; name: string; spotifyArtistId?: string; thumbnail?: string; detailLink?: string };

@Component({
  standalone: true,
  selector: 'app-admin-bands',
  imports: [CommonModule, FormsModule, RouterModule],
  styles: [
    `
    td.editing input { width: 100%; }
    .table-fixed { table-layout: fixed; }
    .id-col { width: 140px; }
    .name-col { width: 260px; }
    .action-col { width: 120px; }
    `
  ],
  template: `
<div class="d-flex align-items-center justify-content-between mb-3">
  <div>
    <a routerLink="/admin" class="btn btn-sm btn-outline-secondary">←<span class="d-none d-lg-inline"> Zurück</span></a>
  </div>
  <h5 class="mb-0">Bands bearbeiten – {{ festivalId }}</h5>
  <div></div>
  
</div>

<div class="mb-3 d-flex flex-wrap gap-2 align-items-end">
  <div>
    <label class="form-label small">Band ID</label>
    <input class="form-control form-control-sm" [(ngModel)]="newBand.bandId" placeholder="z. B. 123">
  </div>
  <div>
    <label class="form-label small">Name</label>
    <input class="form-control form-control-sm" [(ngModel)]="newBand.name" placeholder="Bandname">
  </div>
  <div>
    <label class="form-label small">Spotify Artist ID</label>
    <input class="form-control form-control-sm" [(ngModel)]="newBand.spotifyArtistId" placeholder="spotify artist id">
  </div>
  <div>
    <label class="form-label small">Thumbnail URL</label>
    <input class="form-control form-control-sm" [(ngModel)]="newBand.thumbnail" placeholder="https://...">
  </div>
  <div>
    <label class="form-label small">Detail Link</label>
    <input class="form-control form-control-sm" [(ngModel)]="newBand.detailLink" placeholder="https://...">
  </div>
  <button class="btn btn-sm btn-primary" (click)="createBand()" [disabled]="!newBand.bandId || !newBand.name">Anlegen</button>
</div>

<div class="mb-3 d-flex flex-wrap gap-2 align-items-end">
  <div>
    <label class="form-label small">Suche</label>
    <input class="form-control form-control-sm" [(ngModel)]="filterText" placeholder="Filter nach ID, Name, Spotify ID">
  </div>
  <div class="ms-auto d-flex gap-2">
    <button class="btn btn-sm btn-outline-secondary" (click)="undo()" [disabled]="!undoStack.length">↶ Rückgängig</button>
    <button class="btn btn-sm btn-outline-secondary" (click)="redo()" [disabled]="!redoStack.length">↷ Wiederholen</button>
  </div>
  <div class="text-muted small" *ngIf="undoStack.length">Letzte Änderung: {{ undoStack[undoStack.length-1] | json }}</div>
</div>

<div class="table-responsive">
  <table class="table table-sm align-middle table-striped table-hover table-fixed">
    <thead>
      <tr>
        <th class="id-col">Band ID</th>
        <th class="name-col">Name</th>
        <th>Spotify Artist ID</th>
        <th>Thumbnail URL</th>
        <th>Detail Link</th>
        <th class="action-col">Aktionen</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let b of filtered()">
        <td class="id-col"><code>{{ b.bandId }}</code></td>
        <td (click)="startEdit(b.bandId, 'name')" [class.editing]="isEditing(b.bandId, 'name')">
          <ng-container *ngIf="!isEditing(b.bandId, 'name'); else editName">{{ b.name || '–' }}</ng-container>
          <ng-template #editName>
            <input class="form-control form-control-sm"
                   [(ngModel)]="editBuffer[b.bandId].name"
                   (blur)="commit(b, 'name')"
                   (keydown.enter)="commit(b, 'name')"
                   (keydown.escape)="cancel(b.bandId, 'name')"
                   autofocus>
          </ng-template>
        </td>
        <td (click)="startEdit(b.bandId, 'spotifyArtistId')" [class.editing]="isEditing(b.bandId, 'spotifyArtistId')">
          <ng-container *ngIf="!isEditing(b.bandId, 'spotifyArtistId'); else editSp">{{ b.spotifyArtistId || '–' }}</ng-container>
          <ng-template #editSp>
            <input class="form-control form-control-sm"
                   [(ngModel)]="editBuffer[b.bandId].spotifyArtistId"
                   (blur)="commit(b, 'spotifyArtistId')"
                   (keydown.enter)="commit(b, 'spotifyArtistId')"
                   (keydown.escape)="cancel(b.bandId, 'spotifyArtistId')"
                   autofocus>
          </ng-template>
        </td>
        <td (click)="startEdit(b.bandId, 'thumbnail')" [class.editing]="isEditing(b.bandId, 'thumbnail')">
          <ng-container *ngIf="!isEditing(b.bandId, 'thumbnail'); else editThumb">{{ b.thumbnail || '–' }}</ng-container>
          <ng-template #editThumb>
            <input class="form-control form-control-sm"
                   [(ngModel)]="editBuffer[b.bandId].thumbnail"
                   (blur)="commit(b, 'thumbnail')"
                   (keydown.enter)="commit(b, 'thumbnail')"
                   (keydown.escape)="cancel(b.bandId, 'thumbnail')"
                   autofocus>
          </ng-template>
        </td>
        <td (click)="startEdit(b.bandId, 'detailLink')" [class.editing]="isEditing(b.bandId, 'detailLink')">
          <ng-container *ngIf="!isEditing(b.bandId, 'detailLink'); else editDetail">{{ b.detailLink || '–' }}</ng-container>
          <ng-template #editDetail>
            <input class="form-control form-control-sm"
                   [(ngModel)]="editBuffer[b.bandId].detailLink"
                   (blur)="commit(b, 'detailLink')"
                   (keydown.enter)="commit(b, 'detailLink')"
                   (keydown.escape)="cancel(b.bandId, 'detailLink')"
                   autofocus>
          </ng-template>
        </td>
        <td class="action-col">
          <button class="btn btn-sm btn-outline-danger" (click)="remove(b)">Löschen</button>
        </td>
      </tr>
    </tbody>
  </table>
</div>
  `
})
export class AdminBandsComponent implements OnInit {
  festivalId = '';
  bands = signal<Band[]>([]);
  editing: Record<string, Record<string, boolean>> = {};
  editBuffer: Record<string, Partial<Band>> = {};
  newBand: Partial<Band> = { bandId: '', name: '' };
  filterText = '';

  undoStack: { bandId: string; field: keyof Band; oldValue: any; newValue: any }[] = [];
  redoStack: { bandId: string; field: keyof Band; oldValue: any; newValue: any }[] = [];

  constructor(private route: ActivatedRoute, private api: ApiService) {}

  async ngOnInit() {
    this.route.paramMap.subscribe(async p => {
      const id = p.get('id');
      if (!id) return;
      this.festivalId = id;
      await this.reload();
    });
  }

  private ensureBuffers(list: Band[]) {
    for (const b of list) {
      if (!this.editing[b.bandId]) this.editing[b.bandId] = {};
      if (!this.editBuffer[b.bandId]) this.editBuffer[b.bandId] = { ...b };
    }
  }

  async reload() {
    const list = await this.api.listBands(this.festivalId);
    this.bands.set(list);
    this.ensureBuffers(list);
  }

  filtered() {
    const q = (this.filterText || '').trim().toLowerCase();
    if (!q) return this.bands();
    return this.bands().filter(b =>
      (b.bandId || '').toLowerCase().includes(q) ||
      (b.name || '').toLowerCase().includes(q) ||
      (b.spotifyArtistId || '').toLowerCase().includes(q)
    );
  }

  isEditing(bandId: string, field: keyof Band) {
    return !!this.editing[bandId]?.[field as string];
  }

  startEdit(bandId: string, field: keyof Band) {
    if (!this.editing[bandId]) this.editing[bandId] = {};
    this.editing[bandId][field as string] = true;
    if (!this.editBuffer[bandId]) {
      const b = this.bands().find(x => x.bandId === bandId);
      this.editBuffer[bandId] = { ...b };
    }
  }

  cancel(bandId: string, field: keyof Band) {
    const b = this.bands().find(x => x.bandId === bandId);
    if (b) this.editBuffer[bandId][field] = (b as any)[field];
    this.editing[bandId][field as string] = false;
  }

  async commit(b: Band, field: keyof Band) {
    const value = this.editBuffer[b.bandId][field];
    const oldValue = (b as any)[field];
    if (value === oldValue) {
      this.editing[b.bandId][field as string] = false;
      return;
    }
    await this.api.adminBandUpdate(this.festivalId, b.bandId, { [field]: value } as any);
    // History push
    this.undoStack.push({ bandId: b.bandId, field, oldValue, newValue: value });
    this.redoStack = [];
    // local update
    (b as any)[field] = value as any;
    this.editing[b.bandId][field as string] = false;
  }

  async createBand() {
    if (!this.newBand.bandId || !this.newBand.name) return;
    await this.api.adminBandCreate(this.festivalId, {
      bandId: String(this.newBand.bandId),
      name: String(this.newBand.name),
      spotifyArtistId: this.newBand.spotifyArtistId || undefined,
      thumbnail: this.newBand.thumbnail || undefined,
      detailLink: this.newBand.detailLink || undefined
    });
    this.newBand = { bandId: '', name: '' };
    await this.reload();
  }

  async remove(b: Band) {
    const label = b.name ? `${b.name} (${b.bandId})` : b.bandId;
    const ok = window.confirm(`Band wirklich löschen?\n${label}`);
    if (!ok) return;
    await this.api.adminBandDelete(this.festivalId, b.bandId);
    await this.reload();
  }

  async undo() {
    const last = this.undoStack.pop();
    if (!last) return;
    const { bandId, field, oldValue, newValue } = last;
    await this.api.adminBandUpdate(this.festivalId, bandId, { [field]: oldValue } as any);
    const list = this.bands().map(b => b.bandId === bandId ? { ...b, [field]: oldValue } as Band : b);
    this.bands.set(list);
    if (!this.editBuffer[bandId]) this.editBuffer[bandId] = {};
    this.editBuffer[bandId][field] = oldValue as any;
    this.redoStack.push({ bandId, field, oldValue: newValue, newValue: oldValue });
  }

  async redo() {
    const next = this.redoStack.pop();
    if (!next) return;
    const { bandId, field, oldValue, newValue } = next;
    await this.api.adminBandUpdate(this.festivalId, bandId, { [field]: newValue } as any);
    const list = this.bands().map(b => b.bandId === bandId ? { ...b, [field]: newValue } as Band : b);
    this.bands.set(list);
    if (!this.editBuffer[bandId]) this.editBuffer[bandId] = {};
    this.editBuffer[bandId][field] = newValue as any;
    this.undoStack.push({ bandId, field, oldValue, newValue });
  }
}
